/**
 * «تو هم عضو شو» — پیشنهادِ پیوستن به شبکه، در سه اندازه.
 *
 * چرا جدا و نه چند خط توی هر صفحه: صفحه‌های لینکِ عمومی و لینکِ شخصی تنها
 * جاهایی‌اند که کسی **بدونِ حساب** می‌بیندشان. یعنی تنها نقطه‌ی ورودِ طبیعیِ
 * آدمِ تازه به این شبکه. اگر متنش چند جا نوشته می‌شد، یکی‌شان زود یا دیر از
 * بقیه عقب می‌ماند.
 *
 * لحن عمداً دعوت به **شبکه** است نه به «نصبِ اپ»: طرف همین الان دارد یک
 * دعوتِ واقعی از یک دوستِ واقعی می‌بیند؛ بهترین لحظه‌ی ممکن برای گفتنِ
 * «خودت هم می‌توانی از این‌ها داشته باشی».
 *
 * ═══ چرا سه اندازه ═══
 *
 * چون این پیشنهاد در هر سه جا یک وزن ندارد:
 *
 *   full  لینکِ عمومی، برای کسی که حساب ندارد. آن صفحه جواب نمی‌گیرد، پس
 *         «صفحه‌ی بعدی» هم ندارد و تنها فرصتِ گفتنش همان‌جاست — کارتِ کامل.
 *   link  صفحه‌ی اولِ لینکِ شخصی. کارِ آن صفحه یک چیز است: طرف بفهمد به چه
 *         دعوت شده و جواب بدهد. یک کارتِ تبلیغِ اپ وسطِ آن، همان کاری را
 *         می‌کند که نباید — پس فقط یک لینکِ ریز، ته صفحه.
 *   after بعد از «هستم»/«شاید». حالا طرف واقعاً می‌آید، و سه چیزی که با اپ
 *         گیرش می‌آید (خبرِ تغییر، گفتگو، برنامه‌ی خودش) دیگر تبلیغ نیست،
 *         جوابِ سوالِ «حالا چی؟» است.
 *   app   همان، برای کسی که **از قبل حساب دارد**. فرقش فقط نبودنِ «حساب
 *         بساز» است؛ بقیه‌اش سرِ جایش می‌ماند.
 *
 * ═══ چرا `app` جدا لازم شد ═══
 *
 * چون یک بار این دو تا را یکی گرفتم و اشتباه بود: کلِ کارت پشتِ «حساب ندارد»
 * پنهان شده بود، پس کسی که توی مرورگر وارد شده بود هیچ‌وقت پیشنهادِ نصبِ اپ
 * را نمی‌دید. ولی **وارد بودن در وب هیچ ربطی به نصب بودنِ اپ ندارد** — دقیقاً
 * همان آدم است که هم می‌آید و هم اپ را ندارد. حالا فقط دعوت به *ساختنِ حساب*
 * پشتِ نشست است، نه پیشنهادِ گرفتنِ اپ.
 *
 * ═══ دو راه، نه یکی ═══
 *
 * کارت‌های بزرگ دو خروجی دارند و این عمدی‌ست: «حساب بساز» که همین‌جا توی
 * مرورگر کار می‌کند، و گرفتنِ خودِ اپ برای کسی که روی گوشی است. آن دومی
 * می‌داند روی چه دستگاهی‌ست و روی دسکتاپ به‌جای دکمه QR می‌دهد — همه‌اش در
 * `StoreBadge`، چون همان تصمیم در هر جا باید یکی باشد.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '@/core/lib/i18n';
import { getJoinPitchHidden, hideJoinPitch } from '@/core/lib/joinPitch';
import { Card } from '@/ui/kit';
import { StoreBadge } from '@/ui/StoreBadge';
import { returnHere } from '@/features/auth/returnHere';

export function JoinPitch({ variant = 'full' }: {
  variant?: 'full' | 'link' | 'after' | 'app';
}) {
  const location = useLocation();
  /*
   * پیشنهادِ نرم یعنی بستنی و به‌خاطر سپردنی (WHOSUP-137): مهمانی که یک بار
   * گفت «نمی‌خواهم»، دیگر کارت را نمی‌بیند. لینکِ ریزِ `link` می‌ماند —
   * مزاحم نیست و راهِ برگشت است.
   */
  const [hidden, setHidden] = useState<boolean | null>(null);
  useEffect(() => { getJoinPitchHidden().then(setHidden).catch(() => setHidden(false)); }, []);

  // لینکِ ریز به `/get` می‌رود و نه به ثبت‌نام: روی خودش نوشته «اپ رو بگیر»،
  // پس باید همان‌جا برساندش — و آن صفحه می‌داند کدام فروشگاه.
  if (variant === 'link') {
    return <p className="dim join-link"><Link to="/get">{t('joinSmall')}</Link></p>;
  }

  if (hidden !== false && variant !== 'app') return null;

  const withBullets = variant === 'after' || variant === 'app';

  return (
    <Card className="join-pitch">
      <h3>{variant === 'after' ? t('inviteJoinTitle')
        : variant === 'app' ? t('joinSmall') : t('joinTitle')}</h3>
      {/* متنِ مهمان — نقلِ خواسته‌ی صریح: می‌شود مستقیم جواب داد، یا عضو شد
          و در اپ جواب داد؛ عضویت چت و بقیه‌ی رویدادها را هم باز می‌کند. */}
      {variant !== 'app' ? <p className="dim">{t('joinGuestBody')}</p> : null}
      {withBullets ? (
        <ul className="join-why">
          <li>{t('joinWhyUpdates')}</li>
          <li>{t('joinWhyChat')}</li>
          <li>{t('joinWhyOwn')}</li>
        </ul>
      ) : null}
      {/* تنها چیزی که به حساب داشتن ربط دارد. */}
      {variant !== 'app' ? (
        <Link to="/auth" state={returnHere(location)} className="btn fill block">{t('joinCta')}</Link>
      ) : null}
      <StoreBadge />
      {variant !== 'app' ? (
        <button type="button" className="linklike dim"
          onClick={() => { hideJoinPitch(); setHidden(true); }}>
          {t('joinDismiss')}
        </button>
      ) : null}
    </Card>
  );
}
